import { z } from 'zod';
import { idSchema, instantSchema } from './common.js';
/**
 * Activity ingest (TS-955): other systems post what they sent or received — Relay's Gmail
 * sends, Confirmly's WhatsApp messages — and it lands on the matching candidate's or contact's
 * timeline. `POST /activities` with an API key holding `activity.ingest`.
 *
 * - Idempotent on (organisation, `source`, `externalId`): the same event posted twice is
 *   stored once; the second post answers `duplicate` with the first outcome.
 * - The record is named outright (`entity`) or matched (`match`): by the primary email
 *   (lower-cased, trimmed) or the phone (digits only, so E.164 `+57 300…` and `57300…` are the
 *   same) of live candidates AND contacts. An email that matches is used before a phone.
 *   Every match gets the timeline line, since duplicate people are allowed (RELAY-947).
 * - Nothing matched: the event is kept as `unmatched`, listed for linking, retried by
 *   `:rematch`, and linked by hand with `:link`.
 */
export const INGEST_ACTIVITY_TYPES = [
    'email_sent',
    'email_received',
    'message_sent',
    'message_received',
];
export const ingestActivityTypeSchema = z.enum(INGEST_ACTIVITY_TYPES);
export const INGEST_ENTITY_TYPES = ['candidates', 'contacts'];
export const ingestEntityTypeSchema = z.enum(INGEST_ENTITY_TYPES);
/** A sending system's name: `relay.gmail`, `relay.confirmly`. Part of the idempotency key. */
export const ingestSourceSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z][a-z0-9_.-]{0,63}$/, 'a lower-case system name, e.g. relay.gmail');
/** Largest accepted `payload`, serialised. Subjects and snippets, not whole bodies. */
export const INGEST_PAYLOAD_MAX_BYTES = 16_384;
export const activityIngestSchema = z
    .object({
    type: ingestActivityTypeSchema,
    source: ingestSourceSchema,
    /** The event's id in the sending system (a Gmail message id, a respond.io message id). */
    externalId: z.string().trim().min(1).max(200),
    occurredAt: instantSchema,
    /** The timeline line; derived from `type` and `payload.subject` when omitted. */
    summary: z.string().trim().min(1).max(500).optional(),
    /** Who did it in the sending system, when it is a person: matched to a member by email. */
    actorEmail: z.email().optional(),
    payload: z
        .record(z.string(), z.unknown())
        .default({})
        .refine((p) => JSON.stringify(p).length <= INGEST_PAYLOAD_MAX_BYTES, {
        message: `at most ${INGEST_PAYLOAD_MAX_BYTES} bytes; send a subject or snippet, not the whole message`,
    }),
    entity: z.object({ type: ingestEntityTypeSchema, id: idSchema }).optional(),
    match: z
        .object({
        email: z.string().trim().max(320).optional(),
        phone: z.string().trim().max(40).optional(),
    })
        .optional(),
})
    .refine((e) => e.entity || e.match?.email || e.match?.phone, {
    message: 'name the record (`entity`) or give an email or phone to match it by (`match`)',
    path: ['match'],
});
export const INGEST_STATUSES = ['linked', 'unmatched'];
export const ingestLinkSchema = z.object({
    entityType: ingestEntityTypeSchema,
    entityId: idSchema,
    activityId: idSchema,
});
export const ingestedEventSchema = z.object({
    id: idSchema,
    type: ingestActivityTypeSchema,
    source: z.string(),
    externalId: z.string(),
    occurredAt: instantSchema,
    summary: z.string(),
    status: z.enum(INGEST_STATUSES),
    matchEmail: z.string().nullable(),
    matchPhone: z.string().nullable(),
    links: z.array(ingestLinkSchema),
    receivedAt: instantSchema,
    linkedAt: instantSchema.nullable(),
});
/** `POST /activities`: what happened to the event. `duplicate` repeats the first outcome. */
export const activityIngestResultSchema = ingestedEventSchema.extend({
    duplicate: z.boolean(),
});
export const ingestLinkRequestSchema = z.object({
    entityType: ingestEntityTypeSchema,
    entityId: idSchema,
});
/** `email_sent` + "Interview on Monday" → `Email sent: Interview on Monday`. */
export function ingestSummary(type, payload) {
    const verb = {
        email_sent: 'Email sent',
        email_received: 'Email received',
        message_sent: 'Message sent',
        message_received: 'Message received',
    }[type];
    const channel = typeof payload.channel === 'string' && payload.channel ? payload.channel : null;
    const subject = typeof payload.subject === 'string' && payload.subject ? payload.subject : null;
    const head = channel && type.startsWith('message') ? `${channel} ${verb.toLowerCase()}` : verb;
    const line = subject ? `${head}: ${subject}` : head;
    return line.length > 500 ? `${line.slice(0, 499)}…` : line;
}
//# sourceMappingURL=activity-ingest.js.map